<template>
  <div id="campaignDetail">
    <div class="titlebarBackground"></div>
    <div class="top">
      <div class="campaignSelectors" style="">
        <span>Campaign:</span><br>
        <el-select popper-class="campaignSelectorsPopper" v-model="selectedAdId">
          <el-option
            v-for="(item, idx) in adOptions"
            :key="idx"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
      </div>
      <div class="campaignSelectors" style="display: inline-block">
        <span>Ad:</span><br>
        <el-select v-model="selectedAdType"
                   @clear="selectedAdType = undefined" placeholder="Select an Ad Type">
          <el-option v-for="(v, k) in adTypeOptions" :key="v" :label="k" :value="v">
          </el-option>
        </el-select>
      </div>
    </div>

    <hr class="divider">

    <div class="middle">
      <media-object
        v-if="isAd"
        :theme="theme">
        <img slot="media"
          :src="imageSrc"
          :alt="selectedAd.headline">
        <div slot="content">
          <h3>{{selectedAd.headline}}</h3>
          <p>{{selectedAd.previewText || ''}}</p>
          <p>By: {{campaignData.advertiser}}</p>
        </div>
      </media-object>
      <div v-if="!isAd">
        <h3>{{campaignData.name}}</h3>
      </div>
    </div>

    <div class="bottom"
      v-loading="isLoading"
      element-loading-background="#221f2e">
      <div class="listWrapper">
        <animated-metric
          class="metricWrapper"
          v-for="metric in displayMetrics"
          :key="metric.label"
          :value="getValue(metric)"
          :color="metric.color"
          :label="metric.label"
          :unit="metric.type"
          background-color="#221f2e">
        </animated-metric>
        <div
          class="metricWrapper"
          v-for="n in emptyMetrics"
          :key="n + displayMetrics.length"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiClient from '@/components/api-client'
import MediaObject from '@/components/Layout/MediaObject'
import AnimatedMetric from '@/components/Dashboard/AnimatedMetric'
import {
  map, assign, pick,
  toPairs, concat, filter,
  includes, isNull
} from 'lodash'

export default {
  name: 'campaignDetail',
  props: ['campaignId'],
  components: {
    MediaObject,
    AnimatedMetric
  },
  data () {
    return {
      selectedAdId: null,
      isLoading: false,
      hasInitialData: false,
      pollIntervalId: null,
      oldCampaignData: {'data': []},
      oldAdData: {},
      updateIntervalId: null,
      updatedIndexes: [],
      campaignData: {},
      adData: {},
      selectedAdType: undefined,
      adTypeOptions: null
    }
  },
  mounted () {
    this.$store.commit('setTheme', 'dark')
    this.isLoading = true
    this.getCampaignData().then(() => {
      const adTypes = [...new Set(map(this.adData, 'type'))]

      const options = assign({'All': undefined}, pick({
        'Native Article': 0,
        'Native Display': 2,
        'Scroll-to-Play Video': 5,
        'Click-to-Play Video': 3
      }, adTypes))
      options['All'] = undefined
      this.adTypeOptions = options
    })
  },
  beforeDestroy () {
    clearInterval(this.pollIntervalId)
    this.$store.commit('setTheme', 'normal')
  },
  computed: {
    isAd () {
      return this.selectedAdId !== null
    },
    theme () {
      return this.$store.state.theme
    },
    adOptions () {
      const options = [
        {
          'label': 'Campaign',
          'value': null
        }
      ]
      const ads = map(toPairs(this.adData), (val) => {
        return {
          'value': val[0],
          'label': val[1].name || ''
        }
      })
      return concat(options, ads)
    },
    selectedAd () {
      return this.adData[this.selectedAdId]
    },
    imageSrc () {
      return this.selectedAd.image || '/images/icon-article.svg'
    },
    displayMetrics () {
      let data = []

      if (isNull(this.selectedAdId)) {
        data = this.oldCampaignData.data

        //  When selecting a specific ad type to display aggregates for Native Display ads (e.g. adTypes === 2)
        //  only impressions, clicks, and ctr should be shown.
        if (this.selectedAdType === 2) {
          data = filter(data, (val) => {
            return ['Impressions', 'CTR', 'Clicks'].includes(val['label'])
          })
        }
      } else {
        data = this.oldAdData[this.selectedAdId].data
      }

      return data
    },
    emptyMetrics () {
      return 4 - (this.displayMetrics.length % 4)
    }
  },
  methods: {
    getValue (metric) {
      let value = metric['value']

      if (metric['type'] === 'percent') {
        value = value * 100
      }

      return value
    },
    getCampaignData () {
      return ApiClient.getCampaignDetail(this.campaignId, this.selectedAdType)
        .then(({data}) => {
          this.campaignData = data.campaign
          this.adData = data.ads
          this.oldCampaignData = Object.assign({}, this.campaignData)
          this.oldAdData = Object.assign({}, this.adData)

          this.updateIntervalId = setInterval(this.randomizedMetricUpdate, 2000)
          this.isLoading = false

          if (!isNull(this.pollIntervalId)) {
            clearInterval(this.pollIntervalId)
          }
          this.pollIntervalId = setInterval(() => {
            this.getCampaignData()
              .then(() => {
                this.$set(this.$data, 'updatedIndexes', [])
              })
          }, 120000)
        })
        .catch(({response}) => {
          if (response.status !== 404) {
            this.$notify({
              title: 'Error',
              message: 'Something went wrong.',
              type: 'error'
            })
          }
          this.isLoading = false
        })
    },
    randomizedMetricUpdate () {
      /** Chooses a random index from the metrics array of the chosen ad/campaign and updates it with the new data
       * from the server.
       */
      const doUpdate = (oldArray, newArray, length) => {
        const randIndex = Math.floor(Math.random() * length)

        // Keep track of which indexes have been updated so we don't have long periods of time between specific
        // index updates.
        if (includes(this.updatedIndexes, randIndex)) {
          if (this.updatedIndexes.length === length) {
            return oldArray
          }

          return doUpdate(oldArray, newArray, length)
        }

        return oldArray.map((item, idx) => {
          const newVal = newArray[randIndex].value
          const oldVal = oldArray[randIndex].value

          if (idx === randIndex) {
            if (oldVal === newVal) {
              this.updatedIndexes.push(idx)
              return item
            }

            switch (oldArray[randIndex].type) {
              case 'number':
                const numberInc = Math.floor((newVal - oldVal) / 5) || 1  // eslint-disable-line

                return Object.assign(item, newArray[idx], {
                  value: oldVal <= (newVal - numberInc) ? oldVal + numberInc : newVal
                })
              case 'duration':
                /* eslint-disable */
                const incVal = (newVal - oldVal) / 5
                const durationInc = incVal > 0.1 ? incVal : 0.1
                /* eslint-enable */

                console.debug(`updating index: ${idx}`)

                return Object.assign(item, newArray[idx], {
                  value: oldVal <= (newVal - durationInc) ? oldVal + durationInc : newVal
                })
              default:
                return Object.assign(item, newArray[idx])
            }
          } else {
            return item
          }
        })
      }

      let updatedArray

      // There is a small possibility of `randomizedMetricData` being called before the necessary data is populated
      // since it is called on an interval. We guard against this by skipping the update here and waiting until the
      // next update interval.
      if (isNull(this.selectedAdId)) {
        if (!this.oldCampaignData.data || !this.campaignData.data) {
          // Data isn't ready yet. Defer to the next update in the cycle.
          return
        }

        updatedArray = doUpdate(
          this.oldCampaignData.data,
          this.campaignData.data,
          this.oldCampaignData.data.length
        )
        // Forces vue to rerender on nested array modification.
        this.$set(this.$data.oldCampaignData, 'data', updatedArray)
      } else {
        const newAdData = this.adData[this.selectedAdId]

        if (!newAdData.data) {
          // Data isn't ready yet. Defer till next update interval.
          return
        }

        updatedArray = doUpdate(
          this.oldAdData[this.selectedAdId].data,
          newAdData.data,
          this.oldAdData[this.selectedAdId].data.length
        )
        const newAdObject = Object.assign(
          {},
          this.oldAdData[this.selectedAdId],
          {data: updatedArray}
        )
        // We use set here because directly modifying a nested array will not be picked up by vue's reactivity system.
        this.$set(this.$data.oldAdData, this.selectedAdId, newAdObject)
      }
    }
  },
  watch: {
    selectedAdId () {
      this.$set(this.$data, 'updatedIndexes', [])
      if (!isNull(this.updateIntervalId)) {
        clearInterval(this.updateIntervalId)
        this.updateIntervalId = null
        this.$set(this.$data, 'updatedIndexes', [])
      }

      this.updateIntervalId = setInterval(this.randomizedMetricUpdate, 2000)
    },
    selectedAdType () {
      clearInterval(this.pollIntervalId)
      clearInterval(this.updateIntervalId)

      this.isLoading = true
      this.selectedAdId = null
      this.oldAdData = {}
      this.oldCampaignData = {'data': []}
      this.getCampaignData()
    }
  }
}
</script>

<style lang="scss" scoped>
#campaignDetail {
  margin: 0 auto;
  padding-top: 20px;
  width: 1170px;
  height: 100%;

  .bottom {

    .listWrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;

      @media screen and (max-width: 768px) {
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: nowrap;
      }

      .metricWrapper {
        flex-basis: 24%;
        flex-shrink: 1;
        box-sizing: border-box;
        margin-bottom: 25px;

        @media screen and (max-width: 768px) {
          width: 100%;
        }
      }
    }
  }

  // The title bar is not full width so we need to place a div behind it to make it appear to have a white background.
  .titlebarBackground {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    height: 55px;
    background-color: #fff;
    width: 100vw;

    &.demo {
      height: 106px;
    }

    @media screen and (max-width: 360px) {
      height: 114px;
    }
  }
}
</style>

<style lang="scss">
.main-header {
  z-index: 1;
}
  .campaignSelectors {
  display: inline-block;
  span {
    font-size: 12px;
  }
}

@media screen and (max-width: 480px) {
  .campaignSelectors {
    width: 49%;
    display: inline-block;
    span {
      font-size: 12px;
    }
  }
  .campaignSelectorsPopper {
    width: calc(100vw - 35px);
    left: 16px;
  }
  .metricWrapper {
    margin-bottom: 10px !important;
  }
  .mediaWrapper {
    width: unset !important;
    padding-bottom: 0 !important;
  }
  .contentWrapper {
    padding-top: 0 !important;
  }
}
</style>

